import { alphabeticalCompare } from '@cims-online/shared'
import type {
  RecordsTableFieldColumn,
  RecordsTableFieldProperty,
} from '@cims-online/shared'
import type { TableFilterItem, FilterOptions } from 'hooks/useTableFilter/types'
import type { WorkflowFieldValue, WorkflowRecord } from '../../types'
import { findFieldValue, statusText } from './fieldValues'
import { fieldValueText } from './renderFieldValue'

function selectFilterItems(
  fieldValue: WorkflowFieldValue | undefined,
  property: RecordsTableFieldProperty
): TableFilterItem[] {
  if (!fieldValue) return []
  if (property === 'formattedValue')
    return fieldValue.type === 'generated_int'
      ? [{ value: fieldValue.formattedValue, text: fieldValue.formattedValue }]
      : []
  if (property === 'optionLabel')
    return fieldValue.type === 'selected_by' && fieldValue.value
      ? [
          {
            value: fieldValue.value.optionLabel,
            text: fieldValue.value.optionLabel,
            tooltip: fieldValue.value.optionTooltip,
          },
        ]
      : []
  if (property === 'selectedByUsername')
    return fieldValue.type === 'selected_by' && fieldValue.value
      ? [
          {
            value: fieldValue.value.selectedById,
            text: fieldValue.value.selectedByUsername,
          },
        ]
      : []
  if (property === 'selectedDate') return []

  if (fieldValue.type === 'long_text')
    return fieldValue.value
      ? [{ value: fieldValue.value, text: fieldValue.value }]
      : []
  else if (fieldValue.type === 'generated_int')
    return [{ value: fieldValue.value, text: String(fieldValue.value) }]
  else if (fieldValue.type === 'select')
    return fieldValue.value.map(({ label, optionId, tooltip }) => ({
      value: optionId,
      text: label,
      tooltip,
    }))
  else if (fieldValue.type === 'approvals')
    return fieldValue.value
      .filter(({ optionDeleted }) => !optionDeleted)
      .map(({ optionLabel, optionTooltip }) => ({
        value: optionLabel,
        text: optionLabel,
        tooltip: optionTooltip,
      }))
  else if (fieldValue.type === 'selected_by')
    return fieldValue.value
      ? [
          {
            value: fieldValue.value.optionId,
            text: fieldValue.value.optionLabel,
            tooltip: fieldValue.value.optionTooltip,
          },
        ]
      : []
  else if (fieldValue.type === 'pay_items')
    return fieldValue.value.map(({ payItemId, payItemLabel }) => ({
      value: payItemId,
      text: payItemLabel,
    }))
  return fieldValue.value.map(({ text }) => ({ value: text, text }))
}

function sameFilterValue(
  a: TableFilterItem['value'],
  b: string | number | boolean
) {
  return String(a) === String(b)
}

function uniqueFilterItems(items: TableFilterItem[]) {
  return [
    ...items
      .reduce((unique, item) => {
        const key = String(item.value)
        if (!unique.has(key)) unique.set(key, item)
        return unique
      }, new Map<string, TableFilterItem>())
      .values(),
  ].sort((a, b) => alphabeticalCompare(a.text, b.text))
}

function numberValue(
  fieldValue: WorkflowFieldValue | undefined,
  property: RecordsTableFieldProperty
) {
  if (property !== 'value' || fieldValue?.type !== 'generated_int') return
  return fieldValue.value
}

function dateValue(
  fieldValue: WorkflowFieldValue | undefined,
  property: RecordsTableFieldProperty
) {
  if (property !== 'selectedDate' || fieldValue?.type !== 'selected_by') return
  return fieldValue.value?.selectedDate
}

/**
 * Converts a serialized field filter into the shared table-filter vocabulary.
 *
 * Each operation reads the property named by the server. Select filters can use
 * stable entity IDs or display labels when repeated labels must collapse into
 * one choice.
 */
export function fieldFilter(
  column: RecordsTableFieldColumn
): FilterOptions<WorkflowRecord> | undefined {
  if (!column.filter) return

  const { type, value } = column.filter
  if (type === 'number')
    return {
      type: 'number',
      with: 'value',
      value: (record) => {
        const fieldValue = findFieldValue(record, column.fieldId)?.value
        return numberValue(fieldValue, value)
      },
    }

  if (type === 'search')
    return {
      type: 'search',
      with: 'callback',
      onFilter: (search, record) => {
        const fieldValue = findFieldValue(record, column.fieldId)?.value
        return fieldValueText(fieldValue, value)
          .toLowerCase()
          .includes(String(search).toLowerCase())
      },
    }

  if (type === 'date')
    return {
      type: 'date',
      with: 'value',
      value: (record) =>
        dateValue(findFieldValue(record, column.fieldId)?.value, value),
    }

  return {
    type: 'default',
    with: 'callback',
    items: (records) =>
      uniqueFilterItems(
        column.filter?.type === 'select' && column.filter.items
          ? column.filter.items
          : records.flatMap((record) =>
              selectFilterItems(
                findFieldValue(record, column.fieldId)?.value,
                value
              )
            )
      ),
    onFilter: (selected, record) =>
      selectFilterItems(
        findFieldValue(record, column.fieldId)?.value,
        value
      ).some(({ value }) => sameFilterValue(value, selected)),
  }
}

/**
 * Builds status filter choices from the same derived text used for rendering,
 * including the Draft and Closed system states.
 */
export function statusFilter(): FilterOptions<WorkflowRecord> {
  return {
    type: 'default',
    with: 'callback',
    items: (records) =>
      uniqueFilterItems(
        records.map((record) => ({
          text: statusText(record),
          value: statusText(record),
          tooltip: record.status?.tooltip,
        }))
      ),
    onFilter: (selected, record) => statusText(record) === String(selected),
  }
}
